import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useLiveQuery } from 'dexie-react-hooks';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/field';
import { db } from '@/db';
import { cn } from '@/lib/cn';

const optionalNumber = z.number().min(0).optional();

export const taskFormSchema = z
  .object({
    title: z.string().trim().min(2, 'Titel muss mindestens 2 Zeichen enthalten.').max(120),
    description: z.string().trim().max(1000),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Bitte ein gültiges Datum wählen.'),
    startTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .or(z.literal('')),
    endTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .or(z.literal('')),
    estimatedMinutes: optionalNumber,
    targetValue: optionalNumber,
    unit: z.string().trim().max(30),
    taskType: z.enum([
      'task',
      'checkbox',
      'timed',
      'countable',
      'habit',
      'study',
      'workout',
      'chapter',
      'project',
      'break',
      'sleep'
    ]),
    category: z.enum([
      'school',
      'french',
      'sport',
      'youtube',
      'novel',
      'sleep',
      'personal',
      'routine',
      'break',
      'planning',
      'general'
    ]),
    priority: z.enum(['low', 'normal', 'high', 'mandatory']),
    strictMode: z.enum(['off', 'strict', 'very-strict']),
    projectId: z.string(),
    notes: z.string().trim().max(2000),
    recurrenceKind: z.enum(['once', 'daily', 'weekdays', 'weekly', 'monthly', 'custom']),
    recurrenceInterval: z.number().int().min(1).max(365),
    recurrenceWeekdays: z.array(z.enum(['0', '1', '2', '3', '4', '5', '6'])),
    recurrenceWeekday: z.enum(['0', '1', '2', '3', '4', '5', '6']),
    recurrenceDayOfMonth: z.number().int().min(1).max(31),
    recurrenceUnit: z.enum(['days', 'weeks', 'months']),
    recurrenceOverflow: z.enum(['skip', 'last-day']),
    recurrenceEndDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .or(z.literal('')),
    recurrencePaused: z.boolean()
  })
  .superRefine((value, context) => {
    if (
      value.startTime &&
      value.endTime &&
      value.endTime <= value.startTime &&
      value.taskType !== 'sleep'
    ) {
      context.addIssue({
        code: 'custom',
        path: ['endTime'],
        message: 'Endzeit muss nach der Startzeit liegen.'
      });
    }
    if (['countable', 'chapter', 'workout'].includes(value.taskType) && !value.targetValue) {
      context.addIssue({
        code: 'custom',
        path: ['targetValue'],
        message: 'Für diesen Typ ist ein Zielwert erforderlich.'
      });
    }
    if (value.recurrenceKind === 'weekdays' && value.recurrenceWeekdays.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['recurrenceWeekdays'],
        message: 'Wähle mindestens einen Wochentag.'
      });
    }
    if (value.recurrenceEndDate && value.recurrenceEndDate < value.date) {
      context.addIssue({
        code: 'custom',
        path: ['recurrenceEndDate'],
        message: 'Das Enddatum muss am oder nach dem Start liegen.'
      });
    }
  });

export type TaskFormData = z.infer<typeof taskFormSchema>;

interface TaskFormProps {
  defaultValues: TaskFormData;
  onCancel?: () => void;
  onSubmit: (data: TaskFormData) => Promise<void>;
  submitLabel?: string;
}

const selectClass =
  'min-h-11 w-full appearance-none rounded-xl border border-line bg-canvas px-3.5 text-sm text-ink focus:border-accent';

export function TaskForm({
  defaultValues,
  onCancel,
  onSubmit,
  submitLabel = 'Aufgabe speichern'
}: TaskFormProps) {
  const projects = useLiveQuery(async () => {
    const items = await db.projects.toArray();
    return items.sort((a, b) => a.title.localeCompare(b.title, 'de'));
  }, []);
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch
  } = useForm<TaskFormData>({ resolver: zodResolver(taskFormSchema), defaultValues });
  const taskType = watch('taskType');
  const recurrenceKind = watch('recurrenceKind');
  const recurrenceUnit = watch('recurrenceUnit');
  const needsTarget = ['countable', 'chapter', 'workout'].includes(taskType);

  return (
    <form className="space-y-5" onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
      <Input
        autoFocus
        error={errors.title?.message}
        label="Titel"
        placeholder="Was möchtest du erledigen?"
        {...register('title')}
      />
      <Textarea
        error={errors.description?.message}
        label="Beschreibung"
        placeholder="Optionaler Kontext, Ergebnis oder nächster Schritt"
        rows={3}
        {...register('description')}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Input error={errors.date?.message} label="Datum" type="date" {...register('date')} />
        <label className="space-y-2 text-sm font-semibold text-ink">
          <span className="block">Typ</span>
          <select className={selectClass} {...register('taskType')}>
            <option value="task">Normale Aufgabe</option>
            <option value="checkbox">Checkbox</option>
            <option value="timed">Zeitaufgabe</option>
            <option value="countable">Zählbar</option>
            <option value="habit">Gewohnheit</option>
            <option value="study">Lernaufgabe</option>
            <option value="workout">Training</option>
            <option value="chapter">Kapitelziel</option>
            <option value="project">Projektaufgabe</option>
            <option value="break">Pause</option>
            <option value="sleep">Schlaf</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          error={errors.startTime?.message}
          label="Startzeit"
          type="time"
          {...register('startTime')}
        />
        <Input
          error={errors.endTime?.message}
          label="Endzeit"
          type="time"
          {...register('endTime')}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-2 text-sm font-semibold text-ink">
          <span className="block">Bereich</span>
          <select className={selectClass} {...register('category')}>
            <option value="general">Allgemein</option>
            <option value="school">Schule</option>
            <option value="french">Französisch</option>
            <option value="sport">Sport</option>
            <option value="youtube">YouTube</option>
            <option value="novel">Novel</option>
            <option value="sleep">Schlaf</option>
            <option value="personal">Persönlich</option>
            <option value="routine">Routine</option>
            <option value="planning">Planung</option>
            <option value="break">Pause</option>
          </select>
        </label>
        <label className="space-y-2 text-sm font-semibold text-ink">
          <span className="block">Priorität</span>
          <select className={selectClass} {...register('priority')}>
            <option value="low">Niedrig</option>
            <option value="normal">Normal</option>
            <option value="high">Hoch</option>
            <option value="mandatory">Pflicht</option>
          </select>
        </label>
      </div>

      <div className={cn('grid gap-4', needsTarget ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        <Controller
          control={control}
          name="estimatedMinutes"
          render={({ field }) => (
            <Input
              error={errors.estimatedMinutes?.message}
              label="Dauer (Min.)"
              min={0}
              onChange={(event) =>
                field.onChange(event.target.value === '' ? undefined : Number(event.target.value))
              }
              type="number"
              value={field.value ?? ''}
            />
          )}
        />
        {needsTarget ? (
          <Controller
            control={control}
            name="targetValue"
            render={({ field }) => (
              <Input
                error={errors.targetValue?.message}
                label="Zielwert"
                min={0}
                onChange={(event) =>
                  field.onChange(event.target.value === '' ? undefined : Number(event.target.value))
                }
                type="number"
                value={field.value ?? ''}
              />
            )}
          />
        ) : null}
        <Input
          label="Einheit"
          placeholder={needsTarget ? 'z. B. Kapitel' : 'optional'}
          {...register('unit')}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-2 text-sm font-semibold text-ink">
          <span className="block">Projektzuordnung</span>
          <select className={selectClass} {...register('projectId')}>
            <option value="">Kein Projekt</option>
            {projects?.map((project) => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>
        </label>
        <Textarea label="Notizen" rows={3} {...register('notes')} />
      </div>

      <fieldset className="rounded-xl border border-line bg-canvas p-4">
        <legend className="px-1 text-sm font-semibold text-ink">Wiederholung</legend>
        <div className="mt-2 grid gap-4 sm:grid-cols-2">
          <label className="space-y-2 text-sm font-semibold text-ink">
            <span className="block">Regel</span>
            <select className={selectClass} {...register('recurrenceKind')}>
              <option value="once">Einmalig</option>
              <option value="daily">Täglich</option>
              <option value="weekdays">Bestimmte Wochentage</option>
              <option value="weekly">Wöchentlich</option>
              <option value="monthly">Monatlich</option>
              <option value="custom">Benutzerdefiniertes Intervall</option>
            </select>
          </label>
          {recurrenceKind !== 'once' ? (
            <Controller
              control={control}
              name="recurrenceInterval"
              render={({ field }) => (
                <Input
                  error={errors.recurrenceInterval?.message}
                  label="Intervall"
                  min={1}
                  onChange={(event) => field.onChange(Number(event.target.value))}
                  type="number"
                  value={field.value}
                />
              )}
            />
          ) : (
            <div className="rounded-xl bg-elevated p-3 text-xs leading-relaxed text-muted">
              Die Aufgabe wird nur für das gewählte Datum angelegt.
            </div>
          )}
        </div>

        {recurrenceKind === 'custom' ? (
          <label className="mt-4 block space-y-2 text-sm font-semibold text-ink">
            <span className="block">Intervalleinheit</span>
            <select className={selectClass} {...register('recurrenceUnit')}>
              <option value="days">Tage</option>
              <option value="weeks">Wochen</option>
              <option value="months">Monate</option>
            </select>
          </label>
        ) : null}

        {recurrenceKind === 'weekdays' ||
        (recurrenceKind === 'custom' && recurrenceUnit === 'weeks') ? (
          <div className="mt-4">
            <p className="text-sm font-semibold text-ink">Wochentage</p>
            <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-7">
              {[
                ['1', 'Mo'],
                ['2', 'Di'],
                ['3', 'Mi'],
                ['4', 'Do'],
                ['5', 'Fr'],
                ['6', 'Sa'],
                ['0', 'So']
              ].map(([value, label]) => (
                <label className="cursor-pointer" key={value}>
                  <input
                    className="peer sr-only"
                    type="checkbox"
                    value={value}
                    {...register('recurrenceWeekdays')}
                  />
                  <span className="grid min-h-10 place-items-center rounded-lg border border-line text-xs font-semibold text-muted peer-checked:border-accent peer-checked:bg-accent/10 peer-checked:text-accent">
                    {label}
                  </span>
                </label>
              ))}
            </div>
            {errors.recurrenceWeekdays?.message ? (
              <p className="mt-2 text-sm text-danger">{errors.recurrenceWeekdays.message}</p>
            ) : null}
          </div>
        ) : null}

        {recurrenceKind === 'weekly' ? (
          <label className="mt-4 block space-y-2 text-sm font-semibold text-ink">
            <span className="block">Wochentag</span>
            <select className={selectClass} {...register('recurrenceWeekday')}>
              <option value="1">Montag</option>
              <option value="2">Dienstag</option>
              <option value="3">Mittwoch</option>
              <option value="4">Donnerstag</option>
              <option value="5">Freitag</option>
              <option value="6">Samstag</option>
              <option value="0">Sonntag</option>
            </select>
          </label>
        ) : null}

        {recurrenceKind === 'monthly' ||
        (recurrenceKind === 'custom' && recurrenceUnit === 'months') ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="recurrenceDayOfMonth"
              render={({ field }) => (
                <Input
                  error={errors.recurrenceDayOfMonth?.message}
                  label="Tag im Monat"
                  max={31}
                  min={1}
                  onChange={(event) => field.onChange(Number(event.target.value))}
                  type="number"
                  value={field.value}
                />
              )}
            />
            <label className="space-y-2 text-sm font-semibold text-ink">
              <span className="block">Kurze Monate</span>
              <select className={selectClass} {...register('recurrenceOverflow')}>
                <option value="last-day">Am letzten Tag</option>
                <option value="skip">Überspringen</option>
              </select>
            </label>
          </div>
        ) : null}

        {recurrenceKind !== 'once' ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 sm:items-end">
            <Input
              error={errors.recurrenceEndDate?.message}
              label="Enddatum (optional)"
              type="date"
              {...register('recurrenceEndDate')}
            />
            <label className="flex min-h-11 items-center gap-3 rounded-xl border border-line px-3.5 text-sm font-semibold">
              <input
                className="size-4 accent-[hsl(var(--accent))]"
                type="checkbox"
                {...register('recurrencePaused')}
              />
              Wiederholung pausieren
            </label>
          </div>
        ) : null}
      </fieldset>

      <fieldset className="rounded-xl border border-line bg-canvas p-4">
        <legend className="px-1 text-sm font-semibold text-ink">Verbindlichkeit</legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-3">
          {[
            ['off', 'Flexibel', 'Normal bearbeitbar'],
            ['strict', 'Strikt', 'Ziel heute geschützt'],
            ['very-strict', 'Sehr strikt', 'Wartezeit + Begründung']
          ].map(([value, label, text]) => (
            <label className="cursor-pointer" key={value}>
              <input
                className="peer sr-only"
                type="radio"
                value={value}
                {...register('strictMode')}
              />
              <span className="block min-h-16 rounded-xl border border-line p-3 text-xs text-muted peer-checked:border-accent peer-checked:bg-accent/10 peer-checked:text-ink">
                <strong className="block text-sm">{label}</strong>
                {text}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-col-reverse gap-2 border-t border-line pt-5 sm:flex-row sm:justify-end">
        {onCancel ? (
          <Button onClick={onCancel} type="button" variant="ghost">
            Abbrechen
          </Button>
        ) : null}
        <Button loading={isSubmitting} type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
